import type { ComponentPropsWithoutRef, ElementType, ReactNode } from 'react';
import { Children, cloneElement, createElement, isValidElement } from 'react';
import { cx } from '../utils/cx.js';
import styles from './IconButton.module.css';

function iconChildrenWithAriaHidden(children: ReactNode): ReactNode {
  return Children.map(children, (child) => {
    if (!isValidElement<{ 'aria-hidden'?: boolean; 'aria-label'?: string }>(child)) return child;
    if (child.props['aria-hidden'] != null || child.props['aria-label'] != null) return child;
    return cloneElement(child, { 'aria-hidden': true });
  });
}

export type IconButtonVariant = 'ghost' | 'soft' | 'danger' | 'primary';

/** Default: native `<button>`. Pass `as={Link}` — `to`/`href` from host props. */
export type IconButtonProps<T extends ElementType = 'button'> = {
  as?: T;
  /** Single icon node — pair with `aria-label`. */
  children: ReactNode;
  'aria-label': string;
  variant?: IconButtonVariant;
  size?: 'xs' | 'sm' | 'md';
  /** No border — use inside `Input` suffix / tight rows so outer field owns edge. */
  borderless?: boolean;
  /** Only when `as` is omitted (native `<button>`). */
  type?: HTMLButtonElement['type'];
} & Omit<ComponentPropsWithoutRef<T>, 'as' | 'children' | 'aria-label'>;

export function IconButton<T extends ElementType = 'button'>(props: IconButtonProps<T>) {
  const { as, variant = 'ghost', size = 'sm', borderless, className, type = 'button', children, ...rest } = props;
  const Comp = (as ?? 'button') as ElementType;
  const isNativeButton = !as;
  return createElement(
    Comp,
    {
      ...(isNativeButton ? { type: type ?? 'button' } : {}),
      className: cx(styles.btn, styles[variant], styles[`sz_${size}`], styles.focus, borderless && styles.borderless, className),
      ...rest,
    } as Record<string, unknown>,
    iconChildrenWithAriaHidden(children),
  );
}
